import type { Translations } from './fr'

export const en: Translations = {
  common: {
    loading: 'Loading…',
    error: 'Error',
    success: 'Success',
    info: 'Information',
    cancel: 'Cancel',
    save: 'Save',
    saving: 'Saving...',
    edit: 'Edit',
    delete: 'Delete',
    close: 'Close',
    backToHome: 'Back to home',
    backToDashboard: 'Back to dashboard',
    genericError: 'An error occurred. Please try again.',
    networkError: 'Unable to contact the server. Make sure it is running, then try again.',
    hidden: 'Hidden',
  },
  labels: {
    difficulty: {
      1: 'Level 1 — Syntax',
      2: 'Level 2 — Obvious bug',
      3: 'Level 3 — Visible flaw',
      4: 'Level 4 — Hidden flaws',
      5: 'Level 5 — Expert',
    },
    category: {
      security_owasp: 'Security (OWASP)',
      performance: 'Performance',
      architecture: 'Robustness / Architecture',
      race_condition: 'Concurrency',
      business_logic: 'Business Logic',
    },
    severity: {
      critical: 'Critical',
      major: 'Major',
      minor: 'Minor',
    },
  },
  errors: {
    invalidPassword: 'Current password is incorrect.',
    invalidEmail: 'Invalid email address.',
    emailTaken: 'An account already exists with this email address.',
    passwordTooShort: 'Password must contain at least {min} characters.',
    passwordTooLong: 'Password is too long ({max} characters maximum).',
    passwordMissing: 'Password must contain at least {list}.',
    passwordPolicy: '{min} characters minimum, with at least one lowercase letter, one uppercase letter, one digit and one symbol.',
    ruleLower: 'one lowercase letter',
    ruleUpper: 'one uppercase letter',
    ruleDigit: 'one digit',
    ruleSymbol: 'one symbol',
    listAnd: 'and',
    signUpFailed: 'Registration failed. Please check the information entered.',
    invalidToken: 'The reset link is invalid or has expired.',
    userNotFound: 'User not found.',
    passwordSameAsCurrent: 'The new password must be different from the old one.',
    generic: 'An error occurred. Please verify your input.',
    server: 'The server encountered an error. Please try again shortly.',
  },
  apiErrors: {
    unauthorized: 'Your session has expired. Please log in again.',
    missingParams: 'Choose a language, a difficulty and a category, or Mystery Mode.',
    attemptNotFound: 'Challenge not found.',
    alreadySubmitted: 'This review has already been submitted.',
    invalidComment: 'A remark targets a line missing from the diff, or is empty.',
    rateLimited: 'Too many challenges started recently. Please try again shortly.',
  },
  header: {
    brandAria: 'Code How? Home',
    brandBadge: 'AI Review',
    navAria: 'Main navigation',
    navDemo: 'Code Demo',
    navLanguages: 'Languages',
    navMethod: 'Workflow',
    navProgress: 'Progress',
    home: 'Home',
    dashboard: 'Dashboard',
    accountSettings: 'Account settings',
    accountSettingsFor: 'Account settings for {name}',
    logout: 'Log out',
    login: 'Log in',
    register: 'Sign up',
    switchLangAria: 'Passer en français',
    switchLangTitle: 'Passer en français',
  },
  footer: {
    brandDesc:
      'The training platform for critical code review, application security (OWASP), and Pull Request engineering best practices.',
    platform: 'Platform',
    codeDemo: 'Code Demo',
    supportedLanguages: 'Supported languages',
    methodWorkflow: 'Method & Workflow',
    dashboard: 'Dashboard',
    accountAccess: 'Account & Access',
    logout: 'Log out',
    login: 'Log in',
    register: 'Sign up',
    qualityNorms: 'Quality & Standards',
    wcagNote:
      'Accessible WCAG AA compliant interface with verified contrast and complete keyboard navigation.',
    copyright: '© 2026 Code How? • Web Technologies Project — All rights reserved.',
  },
  auth: {
    loginTitle: 'Sign in to your account',
    registerTitle: 'Create a new account',
    emailLabel: 'Email address',
    emailPlaceholder: 'you@example.com',
    emailRequired: 'Email address is required.',
    passwordLabel: 'Password',
    passwordConfirmLabel: 'Confirm password',
    nameLabel: 'Pseudonym',
    namePlaceholder: 'e.g. thomas',
    nameHint: 'Visible to other users.',
    nameRequired: 'Pseudonym is required.',
    passwordsDoNotMatch: 'Passwords do not match.',
    invalidCredentials: 'Incorrect email address or password.',
    pleaseWait: 'Please wait…',
    signInBtn: 'Sign in',
    signUpBtn: 'Sign up',
    noAccount: "Don't have an account?",
    hasAccount: 'Already have an account?',
    forgotPasswordLink: 'Forgot password?',
    forgotPasswordTitle: 'Forgot password',
    forgotPasswordSubtitle: 'Enter your email address to reset your password.',
    forgotPasswordSubmitBtn: 'Send instructions',
    forgotPasswordSent: 'If an account exists with this address, reset instructions have been prepared.',
    forgotPasswordDemoNotice: 'You can directly use the link generated below to change your password.',
    forgotPasswordDirectBtn: 'Set a new password',
    backToLogin: 'Back to sign in',
    resetPasswordTitle: 'New password',
    resetPasswordSubtitle: 'Choose a new password for your account.',
    resetPasswordBtn: 'Change password',
    resetPasswordSuccess: 'Your password has been changed successfully. You can now sign in.',
    resetPasswordMissingToken: 'The reset token is missing or invalid.',
    requestNewResetLink: 'Request a new link',
  },
  home: {
    welcomeHello: 'Welcome back',
    welcomeReady: '! Ready for a new code review?',
    taglineBadge: 'Code Review training platform for developers',
    heroTitle: 'Master code review.',
    heroTitleHighlight: ' Catch what AI misses.',
    heroDesc:
      "We actively learn to write code, but rarely how to audit others'. Train on realistic multi-language Pull Requests, detect OWASP security vulnerabilities, and sharpen your team's critical thinking.",
    myDashboard: 'Dashboard for {name}',
    exploreDemo: 'Explore code demo',
    createFreeAccount: 'Create free account',
    signIn: 'Sign in',
    metricsAria: 'Key figures',
    metricLanguages: 'Supported languages',
    metricLevels: '5 Levels',
    metricLevelsSub: 'From level 1 (syntax) to level 5 (expert)',
    metricPr: 'GitHub Pull Request style',
    demoPill: 'Interactive Experience',
    demoTitle: 'The anatomy of a true code review',
    demoSubtitle:
      'Inspect business context, review the unified Git diff, comment on suspicious lines, and compare your findings with our AI judge’s verdict.',
    demoTabsAria: 'Demo languages',
    prOpen: 'PR Open',
    modeAnomaly: 'Submitted code (Anomaly)',
    modeSolution: 'Expected solution (Fix)',
    author: 'Author:',
    ticketContext: 'Business ticket context:',
    judgeApproved: 'Analysis validated by the evaluation judge',
    viewSolution: 'View recommended fix',
    languagesPill: 'Multi-language & Coverage',
    languagesTitle: 'Train your eye on modern languages',
    languagesSubtitle:
      'Every ecosystem has distinctive pitfalls and concurrency pitfalls. Explore authentic flaws inspired by real production incidents.',
    targetedFlaws: 'Targeted flaws:',
    mysteryBadge: 'New • Challenge Mode',
    mysteryTitle: 'Ready for a blind audit? Try Mystery Mode',
    mysteryDesc:
      'Language, difficulty, and flaw category are initially hidden. Read the diff with no hints, spot the anomaly, and formulate your critique under real conditions.',
    mysteryBtn: 'Take on Mystery Mode',
    workflowPill: 'Methodology',
    workflowTitle: 'How to level up with Code How?',
    workflowSubtitle:
      'A learning loop engineered to turn code review into a rigorous, constructive engineering habit.',
    step1Title: 'Scenario Selection',
    step1Desc:
      'Filter by favorite language, difficulty level (from 1, a simple syntax error, to 5, subtle flaws), or launch random Mystery Mode.',
    step2Title: 'Git Diff Inspection',
    step2Desc:
      'Explore Jira ticket context and the Pull Request diff with syntax highlighting and suspicious change tracking.',
    step3Title: 'Inline Commenting',
    step3Desc:
      'Click directly on the offending line to state your review feedback, explain the flaw, and propose a recommended alternative.',
    step4Title: 'AI Evaluation & Score',
    step4Desc:
      'Receive a score out of 100, inspect the judge’s detailed debriefing, and discover the verified Ground Truth fix.',
    progressionPill: 'Personal Space',
    progressionTitle: 'Your personal dashboard',
    progressionDesc:
      'Track your score trend over time, analyze performance by theme (OWASP Security, Performance, Architecture), and keep a searchable audit history.',
    pFeature1: 'Live score trend tracking computed in real-time',
    pFeature2: 'Preserved Pull Request and solution history',
    pFeature3: 'Profile, pseudonym, and custom avatar management',
    openDashboard: 'Open my Dashboard',
    createAccountSave: 'Create an account to save progress',
    chartTitle: 'Progress Score • Code How?',
    chartAvg: 'Overall Average',
    chartAudits: 'Completed Audits',
    chartConsistency: 'Consistency',
  },
  dashboard: {
    inProgressTitle: 'Challenges in progress',
    resume: 'Resume',
    chartDate: 'Date',
    chartScore: 'Score out of 100',
    workspaceAria: 'Dashboard workspace',
    heroTitle: 'Your personal',
    heroTitleHighlight: 'progress space',
    heroDesc:
      'Review your audit history, track score trends, and launch new practice challenges.',
    historyTitle: 'History',
    challengesCompleted: '{count} challenges completed',
    newChallenge: 'New challenge',
    loadingHistory: 'Loading history…',
    historyError: 'Unable to load history.',
    emptyHistoryTitle: 'No completed reviews',
    emptyHistorySub: 'Launch your first challenge to start building your audit history.',
    challengeDetails: 'Challenge details',
    excellentAnalysis: 'Excellent analysis',
    goodAnalysis: 'Good analysis',
    needsWork: 'Needs improvement',
    evalScore: 'Evaluation score',
    scoreHelp: 'This score reflects your detection accuracy and feedback relevance.',
    language: 'Language:',
    level: 'Level:',
    category: 'Category:',
    completedOn: 'Completed on {date}',
    viewFullDebrief: 'View full debriefing',
    launchAnother: 'Launch another challenge',
    launcherIntro:
      'Select your criteria to generate a tailored scenario or try Mystery mode for a surprise challenge.',
    progressionTitle: 'Progress',
    filterPrefix: 'Filter:',
    statScenarios: 'Scenarios',
    statAvg: 'Average',
    statBest: 'Best',
    statLast: 'Latest',
    scoreEvolution: 'Score history',
    pointCount: '{count} point{s}',
    noScoreData: 'No score data',
    noScoreDataSub: 'Complete a challenge to visualize your progress curve.',
    auditorRank: 'Auditor rank',
    rankNovice: 'Novice',
    rankNoviceDesc: '{count} more passed review{s} to unlock Apprentice rank.',
    rankApprentice: 'Apprentice Reviewer',
    rankApprenticeDesc: '{count} more passed review{s} to reach Confirmed Reviewer.',
    rankConfirmed: 'Confirmed Reviewer',
    rankConfirmedDesc: '{count} more passed review{s} to reach Senior Auditor rank.',
    rankSenior: 'Senior Auditor 🏆',
    rankSeniorDesc: 'Code review expert with an average score of {avg}%.',
    rankRule: 'Only reviews scored {min}/100 or more count.',
  },
  launcher: {
    language: 'Language',
    difficulty: 'Difficulty',
    category: 'Error category',
    launch: 'Launch challenge',
    mystery: 'Mystery Mode',
    hint: 'Mystery mode randomly selects language, difficulty, and error category without revealing them to you.',
    preparing: 'Preparing pull request…',
    error: 'Failed to create challenge.',
  },
  review: {
    draftsKept: 'Your unsent remarks are kept: resume the game from the dashboard.',
    title: 'Code review',
    loadingPr: 'Loading pull request…',
    evaluating: 'The judge is evaluating your review…',
    loadError: 'Failed to load challenge.',
    submitError: 'Submission failed. Please try again.',
    remarkOnLine: 'Remark on line {line}',
    textareaPlaceholder: 'Describe the issue, its impact, and a suggested fix.',
    saveRemark: 'Save remark',
    yourRemarkLine: 'Your remark, line {line}',
    remarksSidebar: 'Your remarks ({count})',
    sidebarHint:
      'Click a line of code to write a remark on it.',
    submitWithRemarks: 'Submit my review ({count})',
    approveWithoutRemarks: 'Approve without remarks',
    noRemarksYet: 'No remarks yet. Click a suspicious line in the code.',
    approveWarningTitle: 'Approve the PR as is?',
    approveWarning: 'You have not reported any issue. If the PR contains a flaw, it will count as missed.',
    approveConfirm: 'Yes, approve',
    keepReviewing: 'Keep reviewing',
    mission: 'This pull request hides at least one problem. Find it and explain it before submitting your review.',
    stepsAria: 'Game steps',
    step1: 'Read the ticket',
    step2: 'Click the suspicious line',
    step3: 'Explain the problem',
    step4: 'Submit your review',
    help: 'Help',
    helpClose: 'Close help',
    helpTitle: 'How to play?',
    helpIntro: 'You are the reviewer: a colleague asks you to approve their pull request (PR). Your job is to spot what is wrong before the code ships to production.',
    helpReadTitle: 'Reading a diff',
    helpRead: 'Green lines (+) were added by your colleague, red lines (−) removed, the others are unchanged context. Problems hide in the added lines.',
    helpCommentTitle: 'Writing a remark',
    helpComment: 'Click the faulty line (or the + button on the left). A text box opens below it: describe the problem, its consequence and a fix, then save. You can write several remarks and edit them.',
    helpScoreTitle: 'How it is scored',
    helpScore: 'Each flaw found on the right line earns points, and a clear explanation earns more. A remark on a correct line counts as a false positive and lowers the score slightly.',
    helpTipTitle: 'Beginner tip',
    helpTip: 'At level 1, look for a syntax error: missing semicolon, colon, parenthesis or quote. At level 2, a simple bug: wrong comparison, loop going one step too far…',
    prOpen: 'PR open',
    mysteryMode: 'Mystery Mode',
    ticketContext: 'Business ticket context:',
  },
  result: {
    meterAria: '{name}: {value} out of {max}',
    title: 'Debriefing',
    scoreOutOf100: 'Score: {score} out of 100',
    loading: 'Loading debriefing…',
    loadError: 'Failed to load debriefing.',
    anomaliesFound: 'Found anomalies ({found}/{total})',
    explanationQuality: 'Explanation quality',
    precision: 'Precision ({count} false positive{s})',
    judgeSummary: 'Judge synthesis',
    heuristicNotice:
      'The AI judge was unavailable: only flagged lines were compared.',
    expectedIssues: 'Expected anomalies',
    statusFound: 'Found',
    statusMissed: 'Missed',
    statusFalsePositive: 'False positive',
    severity: 'Severity:',
    yourRemarkAtLine: 'Your remark (line {line})',
    judgeFeedbackClarity: 'Judge feedback — clarity {score} / 100',
    expectedFix: 'Expected fix',
    offTargetRemarks: 'Off-target remarks',
    newChallengeBtn: 'New challenge',
  },
  account: {
    title: 'Account Settings',
    avatar: 'Profile picture',
    avatarHint: 'PNG or JPG max 2 MB',
    change: 'Change',
    deletePhoto: 'Delete photo',
    delete: 'Delete',
    themeLabel: 'Interface theme',
    themeDark: 'Dark',
    themeLight: 'Light',
    langLabel: 'Interface language',
    langFr: 'Français',
    langEn: 'English',
    email: 'Email',
    pseudo: 'Pseudonym',
    newPseudo: 'New pseudonym',
    password: 'Password',
    currentPassword: 'Current password',
    newPassword: 'New password',
    confirmNewPassword: 'Confirm new password',
    nameEmpty: 'Pseudonym cannot be empty.',
    fillAllFields: 'Please fill in all fields.',
    passwordsMismatch: 'New passwords do not match.',
    fileTooLarge: 'Image size must not exceed 2 MB.',
    avatarUpdateError: 'Error updating profile picture.',
    avatarRemoveError: 'Error deleting picture.',
    deleteAccount: 'Delete my account',
    deleteAccountHint: 'Permanently erases your account and your whole review history.',
    deleteAccountConfirm: 'This cannot be undone. Enter your password to confirm.',
    deleteAccountFinal: 'Delete permanently',
  },
  diff: {
    added: 'Added line',
    deleted: 'Deleted line',
    unchanged: 'Unchanged line',
    hunk: 'Hunk',
    commentTitle: 'Write a remark on this line',
    commentAria: 'Comment on line {line} of {file}',
  },
  notFound: {
    title: 'Page not found',
    desc: 'This URL does not match any page in the application.',
    returnHome: 'Return to home',
  },
  protected: {
    invalidSession: 'Invalid session',
    incompleteDesc:
      'Your session is recognized but incomplete. Please sign out and sign in again.',
    logout: 'Sign out',
  },
  theme: {
    switchToLight: 'Switch to light theme',
    switchToDark: 'Switch to dark theme',
  },
}
